import React, { useState, useEffect } from 'react';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import './NotebookLM.css';

function NotebookLM({ user }) {
  const [notebooks, setNotebooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [view, setView] = useState('list'); // 'list', 'create', 'notebook'
  const [selectedNotebook, setSelectedNotebook] = useState(null);

  // Create notebook form
  const [newNotebookName, setNewNotebookName] = useState('');
  const [newNotebookDescription, setNewNotebookDescription] = useState('');

  // Document upload
  const [documentName, setDocumentName] = useState('');
  const [documentContent, setDocumentContent] = useState('');
  const [uploading, setUploading] = useState(false);

  // Content generation
  const [generationPrompt, setGenerationPrompt] = useState('');
  const [contentType, setContentType] = useState('report');
  const [outputFormat, setOutputFormat] = useState('text'); // 'text', 'audio', 'video', 'mindmap'
  const [voiceId, setVoiceId] = useState('21m00Tcm4TlvDq8ikWAM'); // Default: Rachel
  const [availableVoices, setAvailableVoices] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [generatedAudio, setGeneratedAudio] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [generatedMindMap, setGeneratedMindMap] = useState(null);

  // Load notebooks and voices on mount
  useEffect(() => {
    if (user) {
      loadNotebooks();
      loadVoices();
    }
  }, [user]);

  const loadVoices = async () => {
    try {
      const getVoices = httpsCallable(functions, 'getElevenLabsVoices');
      const result = await getVoices();

      if (result.data.success && result.data.voices?.voices) {
        setAvailableVoices(result.data.voices.voices);
      }
    } catch (err) {
      console.error('Error loading voices:', err);
      // Continue without voices - audio generation will use default
    }
  };

  const loadNotebooks = async () => {
    setLoading(true);
    setError('');
    try {
      const listNotebooks = httpsCallable(functions, 'listNotebookLMNotebooks');
      const result = await listNotebooks();

      if (result.data.success) {
        // Handle both array and object with notebooks property
        const notebooksList = Array.isArray(result.data.notebooks)
          ? result.data.notebooks
          : result.data.notebooks?.notebooks || [];
        setNotebooks(notebooksList);
      } else {
        const errorMsg =
          result.data.error ||
          result.data.message ||
          'Failed to load notebooks';
        setError(errorMsg);
        console.error('NotebookLM API returned error:', result.data);
      }
    } catch (err) {
      console.error('Error loading notebooks:', err);
      console.error('Full error object:', JSON.stringify(err, null, 2));
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        details: err.details,
        stack: err.stack,
        toString: err.toString(),
      });

      // Handle Firebase function errors with detailed messages
      let errorMessage = 'Failed to load notebooks. ';

      // Extract error message from various possible locations
      let actualErrorMsg = '';
      if (err.message) {
        actualErrorMsg = err.message;
      } else if (err.details) {
        actualErrorMsg = err.details;
      } else if (err.toString && err.toString() !== '[object Object]') {
        actualErrorMsg = err.toString();
      }

      if (err.code) {
        switch (err.code) {
          case 'functions/failed-precondition':
            errorMessage =
              'NotebookLM is not configured. Please contact an administrator to set up GOOGLE_CLOUD_PROJECT_ID in Firebase Functions environment variables.';
            break;
          case 'functions/unauthenticated':
            errorMessage =
              'Authentication failed. Please check your Google Cloud credentials.';
            break;
          case 'functions/permission-denied':
            errorMessage =
              'Permission denied. Please make sure you are logged in and have access to NotebookLM.';
            break;
          case 'functions/not-found':
            errorMessage =
              'NotebookLM function not found. The function may not be deployed yet. Please deploy the Cloud Functions.';
            break;
          case 'functions/internal':
            // Show the actual error message if available
            if (actualErrorMsg && actualErrorMsg.length > 1) {
              errorMessage = `Internal server error: ${actualErrorMsg}`;
            } else {
              errorMessage =
                'Internal server error. The NotebookLM function may not be properly configured. Please check Firebase Functions logs and ensure GOOGLE_CLOUD_PROJECT_ID is set.';
            }
            break;
          default:
            errorMessage = `Error (${err.code}): ${
              actualErrorMsg ||
              'Unknown error. Please check the console for details.'
            }`;
        }
      } else if (actualErrorMsg) {
        errorMessage = actualErrorMsg;
      } else {
        errorMessage += 'Please check the browser console for details.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNotebook = async (e) => {
    e.preventDefault();
    if (!newNotebookName.trim()) {
      setError('Notebook name is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const createNotebook = httpsCallable(
        functions,
        'createNotebookLMNotebook'
      );
      const result = await createNotebook({
        name: newNotebookName,
        description: newNotebookDescription,
      });

      if (result.data.success) {
        setSuccess('Notebook created successfully!');
        setNewNotebookName('');
        setNewNotebookDescription('');
        await loadNotebooks();
        setView('list');
      } else {
        setError('Failed to create notebook');
      }
    } catch (err) {
      console.error('Error creating notebook:', err);
      const errorMessage =
        err.code === 'functions/failed-precondition'
          ? 'NotebookLM is not configured. Please contact an administrator to set up GOOGLE_CLOUD_PROJECT_ID.'
          : err.code === 'functions/unauthenticated'
          ? 'Authentication failed. Please check your Google Cloud credentials.'
          : err.message ||
            err.details ||
            'Failed to create notebook. Please check the console for details.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadDocument = async (e) => {
    e.preventDefault();
    if (!documentName.trim() || !documentContent.trim()) {
      setError('Document name and content are required');
      return;
    }

    if (!selectedNotebook) {
      setError('Please select a notebook first');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const uploadDoc = httpsCallable(functions, 'uploadToNotebookLM');
      // Extract notebook ID from name (full resource path) or use name directly
      const notebookId = selectedNotebook.name || selectedNotebook.id;
      const result = await uploadDoc({
        notebookId: notebookId,
        documentName: documentName,
        content: documentContent,
        mimeType: 'text/markdown',
      });

      if (result.data.success) {
        setSuccess('Document uploaded successfully!');
        setDocumentName('');
        setDocumentContent('');
      } else {
        setError('Failed to upload document');
      }
    } catch (err) {
      console.error('Error uploading document:', err);
      const errorMessage =
        err.code === 'functions/failed-precondition'
          ? 'NotebookLM is not configured. Please contact an administrator to set up GOOGLE_CLOUD_PROJECT_ID.'
          : err.code === 'functions/unauthenticated'
          ? 'Authentication failed. Please check your Google Cloud credentials.'
          : err.message ||
            err.details ||
            'Failed to upload document. Please check the console for details.';
      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateContent = async (e) => {
    e.preventDefault();
    if (!generationPrompt.trim()) {
      setError('Prompt is required');
      return;
    }

    if (!selectedNotebook) {
      setError('Please select a notebook first');
      return;
    }

    setGenerating(true);
    setError('');
    setSuccess('');
    setGeneratedContent(null);
    setGeneratedAudio(null);
    setGeneratedVideo(null);
    setGeneratedMindMap(null);

    try {
      const notebookId = selectedNotebook.name || selectedNotebook.id;
      let result;

      // Route to appropriate function based on output format
      if (outputFormat === 'audio') {
        const generateWithAudio = httpsCallable(
          functions,
          'generateNotebookLMWithAudio'
        );
        result = await generateWithAudio({
          notebookId: notebookId,
          prompt: generationPrompt,
          contentType: contentType,
          voiceId: voiceId,
        });

        if (result.data.success) {
          setGeneratedContent(result.data.content);
          setGeneratedAudio(result.data.audio);
          setSuccess('Content and audio generated successfully!');
        } else {
          setError('Failed to generate content with audio');
        }
      } else if (outputFormat === 'video') {
        const generateVideo = httpsCallable(
          functions,
          'generateNotebookLMVideo'
        );
        result = await generateVideo({
          notebookId: notebookId,
          prompt: generationPrompt,
          contentType: contentType,
          voiceId: voiceId,
        });

        if (result.data.success) {
          setGeneratedContent(result.data.video.content.text);
          setGeneratedVideo(result.data.video);
          setSuccess('Video content generated successfully!');
        } else {
          setError('Failed to generate video');
        }
      } else if (outputFormat === 'mindmap') {
        const generateMindMap = httpsCallable(
          functions,
          'generateNotebookLMMindMap'
        );
        result = await generateMindMap({
          notebookId: notebookId,
          prompt: generationPrompt,
          topic: generationPrompt,
        });

        if (result.data.success) {
          setGeneratedMindMap(result.data.mindMap);
          setGeneratedContent(result.data.rawContent);
          setSuccess('Mind map generated successfully!');
        } else {
          setError('Failed to generate mind map');
        }
      } else {
        // Default: text only
        const generateContent = httpsCallable(
          functions,
          'generateNotebookLMContent'
        );
        result = await generateContent({
          notebookId: notebookId,
          prompt: generationPrompt,
          contentType: contentType,
        });

        if (result.data.success) {
          setGeneratedContent(result.data.content);
          setSuccess('Content generated successfully!');
        } else {
          setError('Failed to generate content');
        }
      }
    } catch (err) {
      console.error('Error generating content:', err);
      const errorMessage =
        err.code === 'functions/failed-precondition'
          ? 'NotebookLM is not configured. Please contact an administrator to set up GOOGLE_CLOUD_PROJECT_ID.'
          : err.code === 'functions/unauthenticated'
          ? 'Authentication failed. Please check your Google Cloud credentials.'
          : err.message ||
            err.details ||
            'Failed to generate content. Please check the console for details.';
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleSelectNotebook = (notebook) => {
    setSelectedNotebook(notebook);
    setView('notebook');
    setGeneratedContent(null);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(clearMessages, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  if (!user) {
    return (
      <div className="notebooklm-error">
        <p>Please log in to use NotebookLM</p>
      </div>
    );
  }

  return (
    <div className="notebooklm-component">
      {/* Messages */}
      {error && (
        <div className="notebooklm-message notebooklm-error-message">
          <span>❌ {error}</span>
          <button onClick={clearMessages} className="message-close">
            ×
          </button>
        </div>
      )}
      {success && (
        <div className="notebooklm-message notebooklm-success-message">
          <span>✅ {success}</span>
          <button onClick={clearMessages} className="message-close">
            ×
          </button>
        </div>
      )}

      {/* Navigation */}
      <div className="notebooklm-nav">
        <button
          onClick={() => {
            setView('list');
            setSelectedNotebook(null);
            setGeneratedContent(null);
          }}
          className={`nav-btn ${view === 'list' ? 'active' : ''}`}
        >
          📚 My Notebooks
        </button>
        <button
          onClick={() => {
            setView('create');
            setSelectedNotebook(null);
            setGeneratedContent(null);
          }}
          className={`nav-btn ${view === 'create' ? 'active' : ''}`}
        >
          ➕ Create Notebook
        </button>
        {selectedNotebook && (
          <button
            onClick={() => setView('notebook')}
            className={`nav-btn ${view === 'notebook' ? 'active' : ''}`}
          >
            📖 {selectedNotebook.displayName || selectedNotebook.name}
          </button>
        )}
      </div>

      {/* List View */}
      {view === 'list' && (
        <div className="notebooklm-view">
          <div className="notebooklm-header">
            <h2>My Notebooks</h2>
            <button
              onClick={loadNotebooks}
              className="refresh-btn"
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <div className="notebooklm-loading">Loading notebooks...</div>
          ) : notebooks.length === 0 ? (
            <div className="notebooklm-empty">
              <p>No notebooks yet. Create one to get started!</p>
            </div>
          ) : (
            <div className="notebooks-grid">
              {notebooks.map((notebook, index) => {
                // Extract display name - could be displayName, name, or from resource path
                const displayName =
                  notebook.displayName ||
                  (notebook.name ? notebook.name.split('/').pop() : null) ||
                  'Untitled';
                const notebookId = notebook.name || notebook.id || index;

                return (
                  <div
                    key={notebookId}
                    className="notebook-card"
                    onClick={() => handleSelectNotebook(notebook)}
                  >
                    <h3>{displayName}</h3>
                    {notebook.description && (
                      <p className="notebook-description">
                        {notebook.description}
                      </p>
                    )}
                    <div className="notebook-meta">
                      <span>
                        📅{' '}
                        {new Date(
                          notebook.createTime ||
                            notebook.createdAt ||
                            Date.now()
                        ).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Create View */}
      {view === 'create' && (
        <div className="notebooklm-view">
          <h2>Create New Notebook</h2>
          <form onSubmit={handleCreateNotebook} className="notebooklm-form">
            <div className="form-group">
              <label htmlFor="notebook-name">Notebook Name *</label>
              <input
                id="notebook-name"
                type="text"
                value={newNotebookName}
                onChange={(e) => setNewNotebookName(e.target.value)}
                placeholder="e.g., Jookbox User Guide"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="notebook-description">
                Description (Optional)
              </label>
              <textarea
                id="notebook-description"
                value={newNotebookDescription}
                onChange={(e) => setNewNotebookDescription(e.target.value)}
                placeholder="Brief description of what this notebook will contain..."
                rows="3"
              />
            </div>
            <div className="form-actions">
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Creating...' : 'Create Notebook'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setView('list');
                  setNewNotebookName('');
                  setNewNotebookDescription('');
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notebook View */}
      {view === 'notebook' && selectedNotebook && (
        <div className="notebooklm-view">
          <div className="notebooklm-header">
            <h2>
              {selectedNotebook.displayName ||
                (selectedNotebook.name
                  ? selectedNotebook.name.split('/').pop()
                  : null) ||
                'Untitled Notebook'}
            </h2>
            <button
              onClick={() => {
                setView('list');
                setSelectedNotebook(null);
              }}
              className="btn-secondary"
            >
              ← Back to List
            </button>
          </div>

          {selectedNotebook.description && (
            <p className="notebook-description-full">
              {selectedNotebook.description}
            </p>
          )}

          {/* Upload Document Section */}
          <div className="notebooklm-section">
            <h3>📄 Upload Document</h3>
            <form onSubmit={handleUploadDocument} className="notebooklm-form">
              <div className="form-group">
                <label htmlFor="doc-name">Document Name *</label>
                <input
                  id="doc-name"
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder="e.g., Getting Started Guide"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="doc-content">
                  Document Content (Markdown) *
                </label>
                <textarea
                  id="doc-content"
                  value={documentContent}
                  onChange={(e) => setDocumentContent(e.target.value)}
                  placeholder="Paste or type your markdown content here..."
                  rows="10"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={uploading}
                className="btn-primary"
              >
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
            </form>
          </div>

          {/* Generate Content Section */}
          <div className="notebooklm-section">
            <h3>✨ Generate Content</h3>
            <form onSubmit={handleGenerateContent} className="notebooklm-form">
              <div className="form-group">
                <label htmlFor="content-type">Content Type</label>
                <select
                  id="content-type"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                >
                  <option value="report">Report</option>
                  <option value="quiz">Quiz</option>
                  <option value="flashcard">Flashcards</option>
                  <option value="summary">Summary</option>
                  <option value="training">Training Material</option>
                  <option value="sales">Sales Presentation</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="output-format">Output Format</label>
                <select
                  id="output-format"
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                >
                  <option value="text">📝 Text Only</option>
                  <option value="audio">🎵 Audio (with ElevenLabs)</option>
                  <option value="video">🎬 Video (Audio + Slides)</option>
                  <option value="mindmap">🗺️ Mind Map</option>
                </select>
              </div>
              {(outputFormat === 'audio' || outputFormat === 'video') && (
                <div className="form-group">
                  <label htmlFor="voice-select">Voice (ElevenLabs)</label>
                  <select
                    id="voice-select"
                    value={voiceId}
                    onChange={(e) => setVoiceId(e.target.value)}
                  >
                    {availableVoices.length > 0 ? (
                      availableVoices.map((voice) => (
                        <option key={voice.voice_id} value={voice.voice_id}>
                          {voice.name}{' '}
                          {voice.labels?.gender && `(${voice.labels.gender})`}
                        </option>
                      ))
                    ) : (
                      <option value="21m00Tcm4TlvDq8ikWAM">
                        Rachel (Default)
                      </option>
                    )}
                  </select>
                </div>
              )}
              <div className="form-group">
                <label htmlFor="generation-prompt">Prompt *</label>
                <textarea
                  id="generation-prompt"
                  value={generationPrompt}
                  onChange={(e) => setGenerationPrompt(e.target.value)}
                  placeholder="e.g., Create a comprehensive guide on how to use Jookbox features..."
                  rows="5"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={generating}
                className="btn-primary"
              >
                {generating
                  ? 'Generating...'
                  : `Generate ${
                      outputFormat === 'text'
                        ? 'Content'
                        : outputFormat === 'audio'
                        ? 'Audio'
                        : outputFormat === 'video'
                        ? 'Video'
                        : 'Mind Map'
                    }`}
              </button>
            </form>
          </div>

          {/* Generated Content Display */}
          {(generatedContent ||
            generatedAudio ||
            generatedVideo ||
            generatedMindMap) && (
            <div className="notebooklm-section">
              <h3>📝 Generated Content</h3>

              {/* Audio Player */}
              {generatedAudio && generatedAudio.audio && (
                <div className="generated-audio">
                  <h4>🎵 Audio</h4>
                  <audio controls className="audio-player">
                    <source
                      src={`data:${
                        generatedAudio.contentType || 'audio/mpeg'
                      };base64,${generatedAudio.audio}`}
                      type={generatedAudio.contentType || 'audio/mpeg'}
                    />
                    Your browser does not support the audio element.
                  </audio>
                  <div className="content-actions">
                    <button
                      onClick={() => {
                        const audioBlob = new Blob(
                          [
                            Uint8Array.from(atob(generatedAudio.audio), (c) =>
                              c.charCodeAt(0)
                            ),
                          ],
                          { type: generatedAudio.contentType || 'audio/mpeg' }
                        );
                        const url = URL.createObjectURL(audioBlob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `notebooklm-audio-${Date.now()}.mp3`;
                        a.click();
                        URL.revokeObjectURL(url);
                        setSuccess('Audio downloaded!');
                      }}
                      className="btn-secondary"
                    >
                      💾 Download Audio
                    </button>
                  </div>
                </div>
              )}

              {/* Video Display */}
              {generatedVideo && (
                <div className="generated-video">
                  <h4>🎬 Video Content</h4>
                  <p className="video-info">
                    Video data generated with{' '}
                    {generatedVideo.content?.slides?.length || 0} slides. Audio
                    is ready, slides structure prepared.
                  </p>
                  {generatedVideo.audio && (
                    <div className="video-audio">
                      <audio controls className="audio-player">
                        <source
                          src={`data:${
                            generatedVideo.audio.contentType || 'audio/mpeg'
                          };base64,${generatedVideo.audio.data}`}
                          type={
                            generatedVideo.audio.contentType || 'audio/mpeg'
                          }
                        />
                      </audio>
                    </div>
                  )}
                  {generatedVideo.content?.slides &&
                    generatedVideo.content.slides.length > 0 && (
                      <div className="video-slides">
                        <h5>Slides ({generatedVideo.content.slides.length})</h5>
                        <div className="slides-list">
                          {generatedVideo.content.slides.map((slide, index) => (
                            <div key={index} className="slide-preview">
                              <h6>
                                Slide {slide.slideNumber}: {slide.title}
                              </h6>
                              <p>{slide.content.substring(0, 200)}...</p>
                              <span className="slide-duration">
                                Duration: ~{slide.duration}s
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  <div className="content-actions">
                    <button
                      onClick={() => {
                        const videoData = JSON.stringify(
                          generatedVideo,
                          null,
                          2
                        );
                        const blob = new Blob([videoData], {
                          type: 'application/json',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `notebooklm-video-data-${Date.now()}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        setSuccess('Video data downloaded!');
                      }}
                      className="btn-secondary"
                    >
                      💾 Download Video Data
                    </button>
                  </div>
                </div>
              )}

              {/* Mind Map Display */}
              {generatedMindMap && (
                <div className="generated-mindmap">
                  <h4>🗺️ Mind Map</h4>
                  <div className="mindmap-visualization">
                    {generatedMindMap.nodes &&
                    generatedMindMap.nodes.length > 0 ? (
                      <div className="mindmap-nodes">
                        {generatedMindMap.nodes.map((node, index) => (
                          <div
                            key={node.id || index}
                            className={`mindmap-node level-${node.level || 0}`}
                            style={{
                              marginLeft: `${(node.level || 0) * 30}px`,
                            }}
                          >
                            <span className="node-label">{node.label}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>
                        Mind map structure generated. Visual rendering coming
                        soon.
                      </p>
                    )}
                  </div>
                  <div className="content-actions">
                    <button
                      onClick={() => {
                        const mindMapData = JSON.stringify(
                          generatedMindMap,
                          null,
                          2
                        );
                        navigator.clipboard.writeText(mindMapData);
                        setSuccess('Mind map data copied to clipboard!');
                      }}
                      className="btn-secondary"
                    >
                      📋 Copy Mind Map Data
                    </button>
                    <button
                      onClick={() => {
                        const mindMapData = JSON.stringify(
                          generatedMindMap,
                          null,
                          2
                        );
                        const blob = new Blob([mindMapData], {
                          type: 'application/json',
                        });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `notebooklm-mindmap-${Date.now()}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        setSuccess('Mind map downloaded!');
                      }}
                      className="btn-secondary"
                    >
                      💾 Download Mind Map
                    </button>
                  </div>
                </div>
              )}

              {/* Text Content Display */}
              {generatedContent && (
                <div className="generated-text">
                  <h4>📝 Text Content</h4>
                  <pre className="content-preview">
                    {typeof generatedContent === 'string'
                      ? generatedContent
                      : JSON.stringify(generatedContent, null, 2)}
                  </pre>
                  <div className="content-actions">
                    <button
                      onClick={() => {
                        const text =
                          typeof generatedContent === 'string'
                            ? generatedContent
                            : JSON.stringify(generatedContent, null, 2);
                        navigator.clipboard.writeText(text);
                        setSuccess('Content copied to clipboard!');
                      }}
                      className="btn-secondary"
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={() => {
                        const text =
                          typeof generatedContent === 'string'
                            ? generatedContent
                            : JSON.stringify(generatedContent, null, 2);
                        const blob = new Blob([text], { type: 'text/plain' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `notebooklm-content-${Date.now()}.txt`;
                        a.click();
                        URL.revokeObjectURL(url);
                        setSuccess('Content downloaded!');
                      }}
                      className="btn-secondary"
                    >
                      💾 Download
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotebookLM;
